import * as React from "react";

import {
  useWorkspaceLoaderData,
  type VisualizerInitData,
} from "@/shared/components/visualization/loader";
import { VizConfigBuilder } from "@/shared/config/VizConfig";
import {
  State,
  VizStateContext,
  VizStateDispatchContext,
  reducer,
} from "@/shared/state/visualization";

/**
 * Provides the visualizer's state and dispatch contexts to the workspace,
 * seeding the initial state from the route loader's result: a restored
 * workspace verbatim, or a fresh state carrying the datasources the loader
 * merged from the URL and the linked event.
 */
export function VisualizationStateProvider({
  children,
}: React.PropsWithChildren) {
  const workspaceResult = useWorkspaceLoaderData();

  // The lazy initializer keeps the VizConfig build from re-running (and being
  // discarded) on every render after the first.
  const [state, dispatch] = React.useReducer(reducer, null, () =>
    initializeVizState(workspaceResult),
  );

  return (
    <VizStateContext.Provider value={state}>
      <VizStateDispatchContext.Provider value={dispatch}>
        {children}
      </VizStateDispatchContext.Provider>
    </VizStateContext.Provider>
  );
}

function initializeVizState(initData: VisualizerInitData): State {
  // A restored workspace is already a complete State; every other source
  // seeds state from the datasources the loader resolved.
  if (initData.configSource?.type === "workspace") {
    return initData.configSource.value;
  }

  const vizConfigBuilder = new VizConfigBuilder();
  vizConfigBuilder.setDatasources(initData.datasources);
  return vizConfigBuilder.build().toObject();
}
